/**
 * 温馨治愈风格角色管理页面
 * 使用柔和配色、圆角、轻柔动画
 */

import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Card,
  Form,
  Input,
  Modal,
  message,
  Tag,
  Switch,
  Tree,
  Row,
  Col,
  Select,
  Divider,
  Typography
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SettingOutlined
} from '@ant-design/icons';
import {
  getRolePage,
  createRole,
  updateRole,
  deleteRole,
  getRoleMenus,
  assignRoleMenus
} from '../services/api/role';
import { getAllMenuTree } from '../services/api/menu';
import { useModal } from '../hooks';
import SearchBar from '../components/common/SearchBar';
import EmptyWarm from '../components/common/EmptyWarm';
import LoadingWarm from '../components/common/LoadingWarm';
import './RoleManager.css';

const { TextArea } = Input;
const { TreeNode } = Tree;
const { Option } = Select;

const RoleManager: React.FC = () => {
  const modal = useModal();
  const menuModal = useModal();
  const [form] = Form.useForm();
  const [menuForm] = Form.useForm();
  const [roles, setRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState({
    keyword: ''
  });
  const [editingRole, setEditingRole] = useState<any>(null);
  const [menuTree, setMenuTree] = useState<any[]>([]);
  const [selectedRole, setSelectedRole] = useState<any>(null);
  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  // 获取角色列表
  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await getRolePage({
        current,
        size: pageSize,
        keyword: searchParams.keyword || undefined
      });
      setRoles(res.records || []);
      setTotal(res.total || 0);
    } catch (error) {
      console.error('获取角色列表失败:', error);
      message.error('获取角色列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchMenuTree();
  }, [current, pageSize, searchParams]);

  // 获取菜单树
  const fetchMenuTree = async () => {
    try {
      const tree = await getAllMenuTree();
      setMenuTree(tree);

      // 设置展开的节点
      const keys: string[] = [];
      const collectKeys = (nodes: any[]) => {
        nodes.forEach(node => {
          keys.push(node.id.toString());
          if (node.children && node.children.length > 0) {
            collectKeys(node.children);
          }
        });
      };
      collectKeys(tree);
      setExpandedKeys(keys);
    } catch (error) {
      console.error('获取菜单树失败:', error);
    }
  };

  // 删除角色
  const handleDelete = async (role: any) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除角色 "${role.name}" 吗？`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deleteRole(role.id);
          message.success('角色删除成功');
          fetchRoles();
        } catch (error) {
          console.error('删除角色失败:', error);
          message.error('删除角色失败');
        }
      }
    });
  };

  // 保存角色
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const data = {
        id: editingRole?.id,
        ...values,
        status: values.status ? 1 : 0
      };

      if (editingRole) {
        await updateRole(data);
        message.success('角色更新成功');
      } else {
        await createRole(data);
        message.success('角色创建成功');
      }

      modal.hideModal();
      form.resetFields();
      setEditingRole(null);
      fetchRoles();
    } catch (error) {
      console.error('操作失败:', error);
    }
  };

  // 分配菜单权限
  const handleAssignMenus = async (role: any) => {
    setSelectedRole(role);
    // 先清空 checkedKeys，避免显示旧数据
    setCheckedKeys([]);
    menuModal.showModal();

    // 获取当前角色的菜单
    try {
      const menuIds = await getRoleMenus(role.id);
      console.log('获取角色菜单返回:', menuIds);
      // 确保是字符串数组，与 Tree 组件的 key 类型匹配
      setCheckedKeys((menuIds || []).map(id => String(id)));
    } catch (error) {
      console.error('获取角色菜单失败:', error);
      setCheckedKeys([]);
    }
  };

  // 保存菜单权限 - 自动添加父级菜单
  const handleSaveMenus = async () => {
    if (!selectedRole) return;

    console.log('保存菜单权限 - selectedRole:', selectedRole);
    console.log('保存菜单权限 - checkedKeys:', checkedKeys);

    try {
      // 将字符串数组转回数字数组
      const selectedMenuIds = checkedKeys.map(k => Number(k));

      // 递归获取所有父级菜单 ID
      const getAllParentIds = (menus: any[], selectedIds: number[]): number[] => {
        const parentIds = new Set<number>();
        const menuMap = new Map<number, any>();

        // 构建菜单映射
        const buildMap = (items: any[]) => {
          items.forEach(item => {
            menuMap.set(item.id, item);
            if (item.children) buildMap(item.children);
          });
        };
        buildMap(menus);

        // 为每个选中的 ID 查找所有父级
        const findParents = (id: number) => {
          const menu = menuMap.get(id);
          if (menu && menu.parentId && menu.parentId !== 0) {
            parentIds.add(menu.parentId);
            findParents(menu.parentId);
          }
        };

        selectedIds.forEach(id => findParents(id));
        return Array.from(parentIds);
      };

      // 获取所有父级菜单 ID
      const parentIds = getAllParentIds(menuTree, selectedMenuIds);

      // 合并选中的 ID 和父级 ID，去重
      const menuIds = [...new Set([...selectedMenuIds, ...parentIds])];

      console.log('保存菜单权限 - 原始选中:', selectedMenuIds);
      console.log('保存菜单权限 - 父级 ID:', parentIds);
      console.log('保存菜单权限 - 最终 menuIds:', menuIds);
      console.log('保存菜单权限 - menuTree:', menuTree);

      await assignRoleMenus(selectedRole.id, menuIds);
      message.success('菜单权限分配成功');
      menuModal.hideModal();
      // 刷新角色列表
      fetchRoles();
    } catch (error) {
      console.error('分配菜单权限失败:', error);
      message.error('分配菜单权限失败');
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '🎭 角色名称',
      dataIndex: 'name',
      key: 'name',
      width: 150
    },
    {
      title: '🎫 角色编码',
      dataIndex: 'code',
      key: 'code',
      width: 150
    },
    {
      title: '📝 描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 200
    },
    {
      title: '📊 状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: number) => (
        <Tag color={status === 1 ? 'success' : 'default'}>
          {status === 1 ? '✅ 正常' : '⏸️ 禁用'}
        </Tag>
      )
    },
    {
      title: '⚙️ 操作',
      key: 'action',
      width: 200,
      render: (_: any, record: any) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingRole(record);
              form.setFieldsValue({
                ...record,
                status: record.status === 1
              });
              modal.showModal();
            }}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            icon={<SettingOutlined />}
            onClick={() => handleAssignMenus(record)}
          >
            权限
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            删除
          </Button>
        </Space>
      )
    }
  ];

  // 渲染菜单树节点
  const renderTreeNodes = (data: any[]) =>
    data.map(item => {
      if (item.children && item.children.length > 0) {
        return (
          <TreeNode title={item.name} key={item.id.toString()} dataRef={item}>
            {renderTreeNodes(item.children)}
          </TreeNode>
        );
      }
      return <TreeNode title={item.name} key={item.id.toString()} dataRef={item} />;
    });

  return (
    <div className="role-manager-container">
      <Card
        className="warm-card"
        title={
          <Space>
            <TeamOutlined className="role-manager-title-icon" />
            <span>角色管理</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingRole(null);
              form.resetFields();
              modal.showModal();
            }}
          >
            🌸 新增角色
          </Button>
        }
        bordered={false}
      >
        <div className="role-manager-search-bar">
          <SearchBar
            placeholder="搜索角色名称或编码..."
            onSearch={(value) => {
              setSearchParams({ keyword: value });
              setCurrent(1);
            }}
            width={320}
          />
        </div>

        {loading ? (
          <LoadingWarm type="tree" tip="角色数据正在路上~" />
        ) : (
          <Table
            dataSource={roles}
            columns={columns}
            rowKey="id"
            pagination={{
              current,
              pageSize,
              total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: (page, size) => {
                setCurrent(page);
                setPageSize(size || 10);
              }
            }}
            locale={{ emptyText: <EmptyWarm type="search" /> }}
          />
        )}
      </Card>

      {/* 新增/编辑角色模态框 */}
      <Modal
        title={editingRole ? '✏️ 编辑角色' : '➕ 新增角色'}
        open={modal.visible}
        onOk={handleSave}
        onCancel={modal.handleCancel}
        confirmLoading={modal.confirmLoading}
        okText="保存"
        cancelText="取消"
        width={600}
        className="warm-modal"
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="🎭 角色名称"
                rules={[{ required: true, message: '请输入角色名称!' }]}
              >
                <Input placeholder="例如：管理员、编辑、访客" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="code"
                label="🎫 角色编码"
                rules={[{ required: true, message: '请输入角色编码!' }]}
              >
                <Input placeholder="例如：admin、editor、guest" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="📝 描述"
          >
            <TextArea
              rows={3}
              placeholder="请输入角色描述"
              maxLength={200}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="status"
            label="📊 状态"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="✅ 正常"
              unCheckedChildren="⏸️ 禁用"
              defaultChecked
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 分配菜单权限模态框 */}
      <Modal
        title={`⚙️ 分配菜单权限 - ${selectedRole?.name || ''}`}
        open={menuModal.visible}
        onOk={handleSaveMenus}
        onCancel={menuModal.handleCancel}
        confirmLoading={menuModal.confirmLoading}
        okText="保存"
        cancelText="取消"
        width={800}
        className="warm-modal"
      >
        <div className="role-manager-menu-tree">
          <Tree
            checkable
            checkStrictly
            expandedKeys={expandedKeys}
            checkedKeys={checkedKeys}
            onCheck={(keys: any) => {
              if (Array.isArray(keys)) {
                setCheckedKeys(keys);
              } else {
                setCheckedKeys(Object.keys(keys.checked || {}));
              }
            }}
            onExpand={(keys) => setExpandedKeys(keys.map(k => k.toString()))}
          >
            {renderTreeNodes(menuTree)}
          </Tree>
        </div>
      </Modal>
    </div>
  );
};

export default RoleManager;
